import type { StudioId } from '@/lib/studios';

// THE REEL (Studio Blueprint 03/05): real outputs only, each tagged with the
// studio that made it and facts a visitor can hold us to (spec plate). Every
// file here is our own engines' output, screened before it was published
// (memory: landing-promo-media-provenance): the corrido cuts come from runs
// df04992a18 / e4563f1ddf, the ad from a real ads-worker job, the story clip
// is our captions over free stock, the storybook cover is the owner's pick
// (#26) from synthetic sample photos, and the quick-tool samples were made by
// scripts/quicktools-samples.cjs from our own media. Nothing is seeded.
//
// `seconds` is the clip as published (ffprobe), not the full product.

export interface ReelItem {
  id: string;
  studio: StudioId;
  /** i18n key under reel.items.<id> for the title. */
  key: string;
  kind: 'video' | 'image';
  src: string;
  poster: string;
  /** width / height of the file. */
  ratio: number;
  seconds?: number;
  /** Where "Make one like this" goes: the studio page, or a quick tool. */
  makePath: string;
  /** Family-safe: shown on family pages' strips; corrido never is. */
  family?: boolean;
  /** "Make one like this" recipe: the corrido style it preselects. */
  style?: string;
  /** The ad spot length (seconds) it preselects. */
  spot?: number;
}

export const REEL: readonly ReelItem[] = [
  { id: 'belico', studio: 'music', key: 'belico', kind: 'video', src: '/promo/corrido_belico.mp4', poster: '/promo/poster_belico.jpg', ratio: 360 / 624, seconds: 5, makePath: '/music', style: 'belico' },
  { id: 'storybook', studio: 'storybook', key: 'storybook', kind: 'image', src: '/storybook/promo/style-animated3d-1200.webp', poster: '/storybook/promo/style-animated3d-640.webp', ratio: 1, makePath: '/storybook', family: true },
  { id: 'energy', studio: 'ads', key: 'energy', kind: 'video', src: '/promo/ad_energy_spot.mp4', poster: '/promo/poster_ad_energy.jpg', ratio: 360 / 640, seconds: 10, makePath: '/ads', spot: 15 },
  { id: 'story', studio: 'story', key: 'story', kind: 'video', src: '/promo/story_clip_1.mp4', poster: '/promo/poster_story_1.jpg', ratio: 302 / 536, seconds: 8, makePath: '/tiktok' },
  { id: 'qt-captions', studio: 'quick', key: 'captions', kind: 'video', src: '/quicktools/captions-sample.mp4', poster: '/quicktools/captions-poster.jpg', ratio: 9 / 16, makePath: '/captions' },
  { id: 'traditional', studio: 'music', key: 'traditional', kind: 'video', src: '/promo/corrido_traditional.mp4', poster: '/promo/poster_traditional.jpg', ratio: 360 / 624, seconds: 5, makePath: '/music', style: 'traditional' },
  { id: 'qt-canvas', studio: 'quick', key: 'canvas', kind: 'video', src: '/quicktools/canvas-sample-v2.mp4', poster: '/quicktools/canvas-poster-v2.jpg', ratio: 9 / 16, makePath: '/canvas' },
  { id: 'trap', studio: 'music', key: 'trap', kind: 'video', src: '/promo/corrido_trap.mp4', poster: '/promo/poster_trap.jpg', ratio: 360 / 624, seconds: 5, makePath: '/music', style: 'trap' },
  { id: 'qt-vows', studio: 'quick', key: 'vows', kind: 'image', src: '/quicktools/vows-sample.jpg', poster: '/quicktools/vows-sample.jpg', ratio: 4 / 5, makePath: '/vows', family: true },
  { id: 'qt-hooks', studio: 'quick', key: 'hooks', kind: 'video', src: '/quicktools/hooks-sample.mp4', poster: '/quicktools/hooks-poster.jpg', ratio: 9 / 16, makePath: '/hooks' },
  { id: 'narco', studio: 'music', key: 'narco', kind: 'video', src: '/promo/corrido_narco.mp4', poster: '/promo/poster_narco.jpg', ratio: 360 / 624, seconds: 5, makePath: '/music', style: 'narco' },
  { id: 'qt-faceless', studio: 'quick', key: 'faceless', kind: 'video', src: '/quicktools/faceless-sample.mp4', poster: '/quicktools/faceless-poster.jpg', ratio: 16 / 9, makePath: '/faceless' },
];

/** The Reel's tabs (the blueprint's stages), in order. */
export const REEL_TABS = ['all', 'music', 'story', 'ads', 'storybook', 'quick'] as const;
export type ReelTab = (typeof REEL_TABS)[number];

export function reelFor(tab: ReelTab): ReelItem[] {
  return tab === 'all' ? [...REEL] : REEL.filter((r) => r.studio === tab);
}

// SAVED RECIPES (blueprint 03: "Save a recipe for later", long-press / S):
// kept on this device only, listed on /projects.
const SAVED_KEY = 'reel:saved-v1';

export function savedRecipes(): string[] {
  try {
    const raw = JSON.parse(window.localStorage.getItem(SAVED_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((id) => REEL.some((r) => r.id === id)) : [];
  } catch {
    return [];
  }
}

export const isSavedRecipe = (id: string) => savedRecipes().includes(id);

/** Saves or unsaves; returns whether it is saved now. */
export function toggleSavedRecipe(id: string): boolean {
  const list = savedRecipes();
  const next = list.includes(id) ? list.filter((x) => x !== id) : [id, ...list];
  try {
    window.localStorage.setItem(SAVED_KEY, JSON.stringify(next));
  } catch {
    /* storage blocked */
  }
  return next.includes(id);
}
